import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import { AuthService } from "src/app/shared/services/authentication/auth.service";
import { PlaysService } from "src/app/shared/services/plays/plays.service";

@Component({
  selector: "app-locations",
  templateUrl: "./locations.component.html",
  styleUrls: ["./locations.component.scss"],
})
export class LocationsComponent implements OnInit, OnDestroy {
  // Stream data
  cityData: any = {};
  countryData: any = {};

  // Video data
  videoCityData: any = {};
  videoCountryData: any = {};

  currentUser: any;
  hasFullAnalyticsAccess = false;
  isLoading = true;
  currentView: "streams" | "videos" = "streams";
  private uiSubscription: Subscription;
  constructor(
    private authService: AuthService,
    private play: PlaysService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.uiSubscription = this.authService.ui$.subscribe((user) => {
      if (user) {
        this.currentUser = user;
        const fullAccessTypes = ["verified", "premium", "label"];
        this.hasFullAnalyticsAccess = fullAccessTypes.includes(
          this.currentUser.creatorType
        );

        if (this.hasFullAnalyticsAccess) {
          this.loadLocationData();
        } else {
          this.isLoading = false; // Stop loading, show upgrade prompt
        }
      } else {
        this.isLoading = false;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.uiSubscription) {
      this.uiSubscription.unsubscribe();
    }
  }

  async loadLocationData() {
    this.isLoading = true;
    try {
      const [streamData, videoData] = await Promise.all([
        this.play.getStats(),
        this.play.getVideoViews(),
      ]);

      if (streamData && streamData.length > 0) {
        this.groupDataByCity(streamData);
        this.groupDataByCountry(streamData);
      }

      if (videoData && videoData.length > 0) {
        this.groupVideoDataByCity(videoData);
        this.groupVideoDataByCountry(videoData);
      }
    } catch (error) {
      console.error("Error loading location data:", error);
    } finally {
      this.isLoading = false;
    }
  }

  groupDataByCountry(data: any[]): void {
    if (!data) return;

    data.forEach((item) => {
      const country = item.country;
      if (!this.countryData[country]) {
        this.countryData[country] = { playCount: 0, uniqueUsers: new Set() };
      }
      this.countryData[country].playCount++;
      this.countryData[country].uniqueUsers.add(item.user);
    });

    // Convert Sets to counts for unique users
    Object.keys(this.countryData).forEach((country) => {
      this.countryData[country].uniqueUserCount =
        this.countryData[country].uniqueUsers.size;
    });
    console.log(this.countryData);
  }

  groupDataByCity(data: any[]): void {
    if (!data) return;

    data.forEach((item) => {
      const city = item.city;
      if (!this.cityData[city]) {
        this.cityData[city] = { playCount: 0, uniqueUsers: new Set() };
      }
      this.cityData[city].playCount++;
      this.cityData[city].uniqueUsers.add(item.user);
    });

    // Convert Sets to counts for unique users
    Object.keys(this.cityData).forEach((city) => {
      this.cityData[city].uniqueUserCount =
        this.cityData[city].uniqueUsers.size;
    });
  }

  groupVideoDataByCountry(data: any[]): void {
    if (!data) return;

    data.forEach((item) => {
      const country = item.country;
      if (!this.videoCountryData[country]) {
        this.videoCountryData[country] = {
          viewCount: 0,
          uniqueUsers: new Set(),
        };
      }
      this.videoCountryData[country].viewCount++;
      this.videoCountryData[country].uniqueUsers.add(item.userId); // Video views use userId
    });

    Object.keys(this.videoCountryData).forEach((country) => {
      this.videoCountryData[country].uniqueUserCount =
        this.videoCountryData[country].uniqueUsers.size;
    });
  }

  groupVideoDataByCity(data: any[]): void {
    if (!data) return;

    data.forEach((item) => {
      const city = item.city;
      if (!this.videoCityData[city]) {
        this.videoCityData[city] = { viewCount: 0, uniqueUsers: new Set() };
      }
      this.videoCityData[city].viewCount++;
      this.videoCityData[city].uniqueUsers.add(item.userId); // Video views use userId
    });

    Object.keys(this.videoCityData).forEach((city) => {
      this.videoCityData[city].uniqueUserCount =
        this.videoCityData[city].uniqueUsers.size;
    });
  }

  navigateToUpgrade() {
    this.router.navigate(["/pricing"]);
  }
}
